import dayjs from 'dayjs';
import { Grid, InputLabel, OutlinedInput } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { DateField, FieldError } from '../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';
import { toApiDate } from '../../../../utils/date';
import { LookupSelect } from '../../shared/lookup-select';
import { SupplierDocumentInput } from '../../shared/supplier-document-input';
import { SUPPLIER_TYPE_OPTIONS } from '../../shared/scp-utils';
import { usePurchaseItemResultController } from './context/purchase-item-result-controller';
import { IPurchaseItemResultResponse } from '../../../../model/scp/purchase-item-result/response/purchase-item-result-response';

const SUPPLIER_SIZE_OPTIONS = [
    { value: 1, label: '1 - ME' },
    { value: 2, label: '2 - EPP' },
    { value: 3, label: '3 - Demais' },
    { value: 4, label: '4 - Não se aplica' },
    { value: 5, label: '5 - Não informado' },
];

const RESULT_SITUATION_OPTIONS = [
    { value: 1, label: '1 - Informado' },
    { value: 2, label: '2 - Cancelado' },
];

export interface PurchaseItemResultDrawerProps {
    purchaseId: number;
    itemNumber: number;
    resultData?: IPurchaseItemResultResponse | null;
    onClose: () => void;
    onSaved: () => void;
}

// Resultado (homologação) de um item publicado; montado apenas enquanto aberto
export default function PurchaseItemResultDrawer({ purchaseId, itemNumber, resultData, onClose, onSaved }: PurchaseItemResultDrawerProps) {
    const ctrl = usePurchaseItemResultController({ purchaseId, itemNumber, resultData, onSaved, onClose });
    const { resultRequest, validationErrors: errors } = ctrl;

    return (
        <FormDrawer
            open
            icon={<AssessmentIcon />}
            title={ctrl.isEditMode ? 'Editar resultado do item' : 'Adicionar resultado do item'}
            subtitle={`Item nº ${itemNumber}`}
            saveLabel="Salvar resultado"
            isSaving={ctrl.isSaving}
            onClose={onClose}
            onSave={ctrl.handleSave}
        >
            <FormDrawerGroup title="Dados do fornecedor">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required label="Tipo de Pessoa" name="personTypeId" value={resultRequest.personTypeId} options={SUPPLIER_TYPE_OPTIONS} onChange={ctrl.handleOnChange} error={errors.personTypeId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SupplierDocumentInput required name="supplierNi" supplierType={resultRequest.personTypeId} value={resultRequest.supplierNi} onChange={ctrl.handleOnChange} error={errors.supplierNi} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Razão Social do Fornecedor</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="supplierCompanyName" value={resultRequest.supplierCompanyName || ''} onChange={ctrl.handleOnChange} error={!!errors.supplierCompanyName} />
                        <FieldError message={errors.supplierCompanyName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required label="Porte do Fornecedor" name="supplierSizeId" value={resultRequest.supplierSizeId} options={SUPPLIER_SIZE_OPTIONS} onChange={ctrl.handleOnChange} error={errors.supplierSizeId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect label="Natureza Jurídica" name="legalNatureId" value={resultRequest.legalNatureId} options={ctrl.legalNatures.map((n) => ({ value: n.id, label: `${n.id} - ${n.name}` }))} onChange={ctrl.handleOnChange} emptyLabel="Selecionar" />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Descrição da Marca</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="descriptionBrand" value={resultRequest.descriptionBrand || ''} onChange={ctrl.handleOnChange} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Valores do resultado" description="O preço total é calculado pela quantidade e pelo preço unitário.">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Quantidade Homologada</InputLabel>
                        <BrazilianNumberInput fullWidth value={resultRequest.approvedQuantity} onChange={(value) => ctrl.update({ approvedQuantity: value })} decimals={4} placeholder="0,0000" error={!!errors.approvedQuantity} />
                        <FieldError message={errors.approvedQuantity} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Preço Unitário Homologado</InputLabel>
                        <BrazilianNumberInput fullWidth value={resultRequest.approvedUnitPrice} onChange={(value) => ctrl.update({ approvedUnitPrice: value })} decimals={4} prefix="R$" placeholder="0,0000" error={!!errors.approvedUnitPrice} />
                        <FieldError message={errors.approvedUnitPrice} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Preço Total Homologado</InputLabel>
                        <BrazilianNumberInput fullWidth value={resultRequest.approvedTotalPrice} disabled decimals={4} prefix="R$" placeholder="0,0000" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Percentual de Desconto</InputLabel>
                        <BrazilianNumberInput fullWidth value={resultRequest.discountPercentage} onChange={(value) => ctrl.update({ discountPercentage: value })} decimals={2} placeholder="0,00" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Data do Resultado" value={toApiDate(dayjs(resultRequest.resultDate))} onChange={ctrl.handleResultDateChange} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            {ctrl.isEditMode && (
                <FormDrawerGroup title="Alteração de resultado">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LookupSelect label="Situação do Resultado do Item" name="situationId" value={resultRequest.situationId} options={RESULT_SITUATION_OPTIONS} onChange={ctrl.handleOnChange} emptyLabel="Selecionar" />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Justificativa</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" multiline rows={2} name="justification" value={resultRequest.justification || ''} onChange={ctrl.handleOnChange} />
                        </Grid>
                    </Grid>
                </FormDrawerGroup>
            )}

            <FormDrawerGroup title="Indicadores">
                <Grid container spacing={1.5} direction="column">
                    <Grid item>
                        <SwitchField label="Indicador de Subcontratação" checked={resultRequest.subcontractingIndicator} onChange={(checked) => ctrl.update({ subcontractingIndicator: checked })} />
                    </Grid>
                    <Grid item>
                        <SwitchField label="Aplicação do Benefício de ME/EPP" checked={resultRequest.meEppBenefitApplication} onChange={(checked) => ctrl.update({ meEppBenefitApplication: checked })} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
